<script setup lang="ts">
  import { computed, useSlots } from "vue";
  import { Plus } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import type { IndexPageLayoutProps } from "./types";

  const slots = useSlots();

  const props = withDefaults(defineProps<IndexPageLayoutProps>(), {
    padding: "default",
    title: undefined,
    subtitle: undefined,
    actionLabel: undefined,
    actionIcon: undefined,
    fill: false,
  });

  const emit = defineEmits<{ action: [] }>();

  const hasSecondRow = computed(() => !!slots.toolbar || !!slots.banner);

  const paddingClass = computed(() => {
    switch (props.padding) {
      case "none":
        return "pt-3 pb-4";
      case "sm":
        return "px-8 pt-3 pb-4";
      default:
        return "px-16 pt-3 pb-4";
    }
  });
</script>

<template>
  <div
    class="font-sans"
    :class="[paddingClass, fill && 'flex h-full flex-col']">
    <div
      class="overflow-hidden rounded-xl border bg-card shadow-sm"
      :class="fill && 'flex min-h-0 flex-1 flex-col'">
      <div
        class="flex flex-col gap-4 px-8 pt-6"
        :class="$slots.banner ? 'pb-5' : $slots.toolbar ? 'pb-7' : 'pb-6'">
        <div
          v-if="title || $slots.filters || $slots.actions || actionLabel"
          class="flex items-center justify-between gap-4">
          <h1
            v-if="title"
            class="heading-page flex items-baseline gap-3">
            {{ title }}
            <span
              v-if="subtitle"
              class="text-sm font-normal text-muted-foreground">
              {{ subtitle }}
            </span>
          </h1>
          <div class="ml-auto flex items-center gap-2">
            <slot
              v-if="!hasSecondRow"
              name="filters" />
            <slot name="actions" />
            <Button
              v-if="actionLabel"
              class="shadow-md"
              @click="emit('action')">
              <component :is="actionIcon ?? Plus" />
              {{ actionLabel }}
            </Button>
          </div>
        </div>
      </div>

      <div
        v-if="$slots.banner"
        class="px-8 pb-4">
        <slot name="banner" />
      </div>

      <div
        v-if="$slots.toolbar || ($slots.filters && hasSecondRow)"
        class="flex items-end gap-4 px-8"
        :class="!$slots.toolbar && 'border-b'">
        <slot name="toolbar" />
        <div
          v-if="$slots.filters"
          class="ml-auto flex items-center gap-2 pb-2">
          <slot name="filters" />
        </div>
      </div>

      <div
        :class="[
          'px-8 pb-5 [&>div:has(table)]:-mx-8 [&_td:first-child:not([data-slot=detail])]:pl-8! [&_td:last-child:not([data-slot=detail])]:pr-8! [&_th:first-child]:pl-8! [&_th:last-child]:pr-8!',
          hasSecondRow && 'pt-4',
          fill && 'min-h-0 flex-1',
        ]">
        <slot />
      </div>
    </div>

    <slot name="dialogs" />
  </div>
</template>
